import { DashboardFlow, useDashboardFlowNavigation } from '@melio/ar-dashboard';
import {
  addWildcardToRoutes,
  Navigate,
  useLocation,
  useNavigate,
  withLocalization,
  withRouteId,
} from '@melio/ar-domain';
import {
  CreateInvoiceFlow,
  EditInvoiceFlow as _EditInvoiceFlow,
  PreviewInvoiceFlow as _PreviewInvoiceFlow,
} from '@melio/ar-invoice-lifecycle';
import { useState } from 'react';
import { Route, Routes } from 'react-router-dom';

const EditInvoiceFlow = withRouteId(_EditInvoiceFlow, 'invoiceId');
const PreviewInvoiceFlow = withRouteId(_PreviewInvoiceFlow, 'invoiceId');

type InvoiceDashboardRouterProps = {
  WithInitialData: React.ComponentType;
  DashboardLayout: React.ComponentType;
};

export const InvoiceDashboardRouter = ({ DashboardLayout, WithInitialData }: InvoiceDashboardRouterProps) => {
  const {
    RedirectPaths,
    Paths,
    createdInvoiceId,
    goToDashboard,
    goToEditInvoice,
    goToCreateInvoice,
    goToPreviewInvoice,
    onCreateInvoiceCancel,
  } = useARRouterNavigation();

  return (
    <Routes>
      <Route element={<WithInitialData />}>
        <Route index element={<Navigate to={RedirectPaths.Dashboard} withSearchparams replace />} />
        <Route
          path={Paths.CreateInvoice}
          element={withLocalization(<CreateInvoiceFlow onDone={goToDashboard} onClose={onCreateInvoiceCancel} />)}
        />
        <Route
          path={Paths.EditInvoice}
          element={withLocalization(<EditInvoiceFlow onDone={goToDashboard} onClose={goToDashboard} />)}
        />
        <Route path={Paths.PreviewInvoice} element={withLocalization(<PreviewInvoiceFlow onClose={goToDashboard} />)} />
        <Route element={<DashboardLayout />}>
          <Route
            path={Paths.Dashboard}
            element={withLocalization(
              <DashboardFlow
                onEditInvoice={goToEditInvoice}
                onCreateInvoice={goToCreateInvoice}
                onPreviewInvoice={goToPreviewInvoice}
                createdInvoiceId={createdInvoiceId}
              />
            )}
          />
        </Route>
      </Route>
    </Routes>
  );
};
InvoiceDashboardRouter.displayName = 'InvoiceDashboardRouter';

type ARRouterLocationState = { from: 'customers' | 'invoices' };

const useARRouterNavigation = () => {
  enum Paths {
    Dashboard = 'dashboard',
    CreateInvoice = 'invoice/new/edit',
    EditInvoice = 'invoice/:id/edit',
    PreviewInvoice = 'invoice/:id/preview',
  }

  const navigate = useNavigate<Paths, ARRouterLocationState>({ withSearchparams: true });
  const location = useLocation<ARRouterLocationState>();

  const dashboardRouter = useDashboardFlowNavigation(Paths.Dashboard, { withSearchparams: true });

  const [createdInvoiceId, setCreatedInvoiceId] = useState<string>();
  const reset = () => setCreatedInvoiceId(undefined);

  const goToDashboard = (id?: string) => {
    setCreatedInvoiceId(id);
    navigate(Paths.Dashboard);
  };

  const goToEditInvoice = (id: string) => {
    reset();
    navigate(Paths.EditInvoice, { pathParams: { id } });
  };

  const goToCreateInvoice = (queryParams?: { customerId?: string }) => {
    reset();
    navigate(Paths.CreateInvoice, { queryParams, state: { from: queryParams?.customerId ? 'customers' : 'invoices' } });
  };

  const goToPreviewInvoice = (id: string) => {
    reset();
    navigate(Paths.PreviewInvoice, { pathParams: { id } });
  };

  const onCreateInvoiceCancel = () => {
    if (location.state?.from === 'customers') dashboardRouter.goToCustomersDashboard();
    else dashboardRouter.goToInvoicesDashboard();
  };

  return {
    RedirectPaths: Paths,
    Paths: addWildcardToRoutes(Paths),
    createdInvoiceId,
    goToDashboard,
    goToEditInvoice,
    goToCreateInvoice,
    onCreateInvoiceCancel,
    goToPreviewInvoice,
  };
};
